import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { cmsApiConfig } from '../../cmsConfig';
import { UpdatePersonalInformation } from '../../types/Coach/UpdatePersonalInformation';
import { SolutionTypeList } from '../../types/Coach/SolutionType';
export interface ReactConfig {
  applicationInsightsConnectionString: string;
}

type ResolveParticipantIdentifierResponse =
  | string
  | {
      participantId?: string;
      participantGuid?: string;
      id?: string;
    };

export const portalBaseApi = createApi({
  reducerPath: 'portalBaseApi',
  baseQuery: fetchBaseQuery({
    baseUrl: `${cmsApiConfig.url}/portalapi`,
    headers: {
      'X-CSRF': '1',
    },
    credentials: 'include',
  }),
  tagTypes: ['ParticipantDetails', 'SolutionTypes'],
  endpoints: (builder) => ({
    updatePersonalInformation: builder.mutation<
      void,
      { id: string; body: UpdatePersonalInformation }
    >({
      query: ({ id, body }) => ({
        url: `/personalInformation?participantId=${id}`,
        method: 'PUT',
        body: body,
      }),
      invalidatesTags: (result, error, { id }) => {
        return [{ type: 'ParticipantDetails', id }];
      },
    }),
    getSolutionTypes: builder.query<SolutionTypeList[], void>({
      query: () => ({
        url: `/participationSolutions`,
      }),
      transformResponse: (response: SolutionTypeList[]) => {
        const sortedList = response.sort((a, b) =>
          a.key.name.localeCompare(b.key.name)
        );
        return sortedList;
      },
      providesTags: ['SolutionTypes'],
    }),
    resolveParticipantIdentifier: builder.query<string, string>({
      query: (participantKey) => ({
        url: `/participant/resolve?participantKey=${encodeURIComponent(participantKey)}`,
        responseHandler: async (response) => {
          const contentType = response.headers.get('content-type') || '';

          if (contentType.includes('application/json')) {
            return response.json();
          }

          return response.text();
        },
      }),
      transformResponse: (response: ResolveParticipantIdentifierResponse) => {
        if (typeof response === 'string') {
          const trimmedResponse = response.trim();

          if (
            trimmedResponse.startsWith('{') &&
            trimmedResponse.endsWith('}')
          ) {
            try {
              const parsedResponse = JSON.parse(
                trimmedResponse
              ) as ResolveParticipantIdentifierResponse;

              if (typeof parsedResponse === 'string') {
                return parsedResponse;
              }

              return (
                parsedResponse.participantId ||
                parsedResponse.participantGuid ||
                parsedResponse.id ||
                ''
              );
            } catch {
              return trimmedResponse;
            }
          }

          return trimmedResponse;
        }

        return (
          response.participantId ||
          response.participantGuid ||
          response.id ||
          ''
        );
      },
    }),
  }),
});

export const {
  useUpdatePersonalInformationMutation,
  useGetSolutionTypesQuery,
  useLazyGetSolutionTypesQuery,
  useResolveParticipantIdentifierQuery,
} = portalBaseApi;
